import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import {
  CounterPattern,
  CounterPatternMap,
  DeliveryStatus,
  DeliveryStatusMap,
  SaleType,
  SaleTypeMap,
} from 'features/common/models';
import { ApprovalStatus, ApprovalStatusMap } from 'features/common/models/approvalStatus';
import { CommonResponse, SortOrderType } from 'models';
import { Delivery, DeliveryInfo, DeliveryMasterDataInfo, DeliverySearchPayload } from './models/delivery';
import { sortDeliveryData } from './utils';

export interface DeliveryState {
  loading: boolean;
  master_data: {
    delivery_status: DeliveryStatus[];
    approval_status: ApprovalStatus[];
    sale_type: SaleType[];
    counter_pattern: CounterPattern[];
  };
  deliveries: Delivery[];
  sortBy: string;
  sortOrder: SortOrderType;
  workflowActions: string[];
  isLinkDisabled: boolean;
  asyncCreationTask: {
    taskId: string | null;
    status: 'idle' | 'started' | 'progressing' | 'completed' | 'failed';
    progress: string;
    error: string | null;
    startTime: string | null;
    showCompletionToast: boolean; // Flag to show toast on page load
  };
}

const initialState: DeliveryState = {
  loading: false,
  master_data: {
    delivery_status: [],
    approval_status: [],
    sale_type: [],
    counter_pattern: [],
  },
  deliveries: [],
  sortBy: 'id',
  sortOrder: 'desc',
  workflowActions: [],
  isLinkDisabled: false,
  asyncCreationTask: {
    taskId: null,
    status: 'idle',
    progress: '',
    error: null,
    startTime: null,
    showCompletionToast: false,
  },
};

const deliveryListSlice = createSlice({
  name: 'delivery',
  initialState,
  reducers: {
    //fetch delivery data
    fetchDeliveryMasterData(state) {
      state.loading = true;
    },
    fetchDeliveryMasterDataSuccess(state, action: PayloadAction<CommonResponse<DeliveryMasterDataInfo>>) {
      // approval_status
      const newApprovalStatusValues = action.payload.data.approval_status.map<ApprovalStatus>((item) => ({
        id: item[0],
        name: item[1],
      }));
      state.master_data.approval_status = newApprovalStatusValues;

      // delivery_status
      const newDeliveryStatusValues = action.payload.data.delivery_status.map<DeliveryStatus>((item) => ({
        id: item[0],
        name: item[1],
      }));
      state.master_data.delivery_status = newDeliveryStatusValues;

      state.master_data.sale_type = action.payload.data.sale_type;
      state.master_data.counter_pattern = action.payload.data.counter_pattern;
      state.loading = false;
    },
    fetchDeliveryMasterDataFailed(state) {
      state.loading = false;
    },

    //update sortBy
    updateFilterSort(state, action: PayloadAction<{ sortBy: string; sortOrder: SortOrderType }>) {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
      sortDeliveryData(state.deliveries, action.payload.sortBy, action.payload.sortOrder);
    },

    //fetchDeliveriesInfo
    fetchDeliveriesInfo(state, action: PayloadAction<DeliverySearchPayload>) {
      state.loading = true;
    },
    fetchDeliveriesInfoSuccess(state, action: PayloadAction<CommonResponse<DeliveryInfo>>) {
      state.deliveries = sortDeliveryData(action.payload.data.deliveries, state.sortBy, state.sortOrder);
      state.loading = false;
    },
    fetchDeliveriesInfoDataFailed(state) {
      state.loading = false;
    },

    //performWorkflowAction
    performWorkflowAction(
      state,
      action: PayloadAction<{
        deliveryId: number;
        action: string;
        filterSearch: DeliverySearchPayload;
        employeeJobId: number;
      }>
    ) {
      state.loading = true;
    },
    performWorkflowActionSuccess(state) {
      state.loading = false;
    },
    performWorkflowActionFailed(state) {
      state.loading = false;
    },

    //fetchWorkflowActions
    fetchWorkflowActions(state, action: PayloadAction<number>) {
      state.loading = true;
    },
    fetchWorkflowActionsSuccess(state, action: PayloadAction<any>) {
      state.workflowActions = action.payload;
      state.loading = false;
    },
    fetchWorkflowActionsFailed(state) {
      state.loading = false;
    },
    updateIsLinkDisabled(state, action: PayloadAction<boolean>) {
      state.isLinkDisabled = action.payload;
    },
    
    // Async Creation Task Management
    setAsyncCreationTaskStarted(state, action: PayloadAction<{ taskId: string; progress: string }>) {
      state.asyncCreationTask = {
        taskId: action.payload.taskId,
        status: 'started',
        progress: action.payload.progress,
        error: null,
        startTime: new Date().toISOString(),
        showCompletionToast: false,
      };
    },
    setAsyncCreationTaskProgressing(state, action: PayloadAction<{ progress: string }>) {
      if (state.asyncCreationTask.taskId) {
        state.asyncCreationTask.status = 'progressing';
        state.asyncCreationTask.progress = action.payload.progress;
      }
    },
    setAsyncCreationTaskCompleted(state, action: PayloadAction<{ showToast?: boolean }>) {
      if (state.asyncCreationTask.taskId) {
        state.asyncCreationTask.status = 'completed';
        state.asyncCreationTask.progress = 'Delivery created successfully';
        state.asyncCreationTask.showCompletionToast = action.payload.showToast ?? false;
      }
    },
    setAsyncCreationTaskFailed(state, action: PayloadAction<{ error: string; showToast?: boolean }>) {
      if (state.asyncCreationTask.taskId) {
        state.asyncCreationTask.status = 'failed';
        state.asyncCreationTask.error = action.payload.error;
        state.asyncCreationTask.showCompletionToast = action.payload.showToast ?? false;
      }
    },
    resetAsyncCreationTask(state) {
      state.asyncCreationTask = {
        taskId: null,
        status: 'idle',
        progress: '',
        error: null,
        startTime: null,
        showCompletionToast: false,
      };
    },
    clearCompletionToastFlag(state) {
      state.asyncCreationTask.showCompletionToast = false;
    },

    // Update single delivery item's reedit_status
    updateDeliveryReeditStatus(state, action: PayloadAction<{ deliveryId: number; reeditStatus: number }>) {
      const delivery = state.deliveries.find(d => d.id === action.payload.deliveryId);
      if (delivery) {
        delivery.reedit_status = action.payload.reeditStatus;
      }
    },
  },
});

// Actions
export const deliveryListActions = deliveryListSlice.actions;

// Selectors
// export const selectDeliveryListLoading = (state: RootState) => state.delivery.loading;
export const selectDeliveriesData = (state: RootState) => state.delivery.deliveries;
export const selectDeliveryDeliveryStatus = (state: RootState) => state.delivery.master_data.delivery_status;
export const selectDeliveryApprovalStatus = (state: RootState) => state.delivery.master_data.approval_status;
export const selectDeliverySaleType = (state: RootState) => state.delivery.master_data.sale_type;
export const selectDeliveryCounterPattern = (state: RootState) => state.delivery.master_data.counter_pattern;
export const selectDeliverySortBy = (state: RootState) => state.delivery.sortBy;
export const selectDeliverySortOrder = (state: RootState) => state.delivery.sortOrder;
export const selectDeliveryWorkflowActions = (state: RootState) => state.delivery.workflowActions;
export const selectIsLinkDisabled = (state: RootState) => state.delivery.isLinkDisabled;
export const selectAsyncCreationTask = (state: RootState) => state.delivery.asyncCreationTask;
export const selectAsyncCreationTaskId = (state: RootState) => state.delivery.asyncCreationTask.taskId;
export const selectAsyncCreationTaskStatus = (state: RootState) => state.delivery.asyncCreationTask.status;
export const selectAsyncCreationTaskProgress = (state: RootState) => state.delivery.asyncCreationTask.progress;
export const selectAsyncCreationTaskError = (state: RootState) => state.delivery.asyncCreationTask.error;
export const selectAsyncCreationShowCompletionToast = (state: RootState) => state.delivery.asyncCreationTask.showCompletionToast;

export const selectDeliveryDeliveryStatusMap = createSelector(selectDeliveryDeliveryStatus, (deliveryStatusList) => {
  return deliveryStatusList.reduce((deliveryStatusMap: DeliveryStatusMap, deliveryStatus: DeliveryStatus) => {
    deliveryStatusMap[deliveryStatus.id] = deliveryStatus.name;
    return deliveryStatusMap;
  }, {});
});

export const selectDeliveryApprovalStatusMap = createSelector(selectDeliveryApprovalStatus, (approvalStatusList) => {
  return approvalStatusList.reduce((approvalStatusMap: ApprovalStatusMap, approvalStatus: ApprovalStatus) => {
    approvalStatusMap[approvalStatus.id] = approvalStatus.name;
    return approvalStatusMap;
  }, {});
});

export const selectDeliverySaleTypeMap = createSelector(selectDeliverySaleType, (saleTypes) => {
  return saleTypes.reduce((saleTypeMap: SaleTypeMap, saleType: SaleType) => {
    saleTypeMap[saleType.id] = saleType.name;
    return saleTypeMap;
  }, {});
});

export const selectDeliveryCounterPatternMap = createSelector(selectDeliveryCounterPattern, (counterPatterns) => {
  return counterPatterns.reduce((counterPatternMap: CounterPatternMap, counterPattern: CounterPattern) => {
    counterPatternMap[counterPattern.id] = counterPattern.cd + ' ' + counterPattern.name;
    return counterPatternMap;
  }, {});
});

// Reducer
export const deliveryReducer = deliveryListSlice.reducer;
